import {
  type EditImportPreviewEntryInput,
  type ImportJob,
  type ImportPreviewEntry,
  type ImportRawRow,
  importKeys,
} from '@taler/api-client'
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query'
import { computed, type ComputedRef, watch } from 'vue'
import { retrying } from '@/app/retrying'
import { isNotFound } from '@/app/not-found'
import { useApi } from '@/app/api'
import { problemCode, problemDetail } from '../internal/problem.ts'

export interface UseImportJobOptions {
  readonly ledgerId: () => string | undefined
  readonly jobId: () => string | undefined
  readonly enabled: () => boolean
}

export interface UseImportJob {
  readonly job: ComputedRef<ImportJob | undefined>
  readonly rawRows: ComputedRef<readonly ImportRawRow[]>
  readonly previewEntries: ComputedRef<readonly ImportPreviewEntry[]>
  readonly isLoading: ComputedRef<boolean>
  readonly error: ComputedRef<Error | null>
  readonly notFound: ComputedRef<boolean>
  readonly retrying: ComputedRef<boolean>
  readonly isSaving: ComputedRef<boolean>
  readonly saveErrorDetail: ComputedRef<string | undefined>
  readonly isCommitting: ComputedRef<boolean>
  readonly commitErrorDetail: ComputedRef<string | undefined>
  readonly commitErrorCode: ComputedRef<string | undefined>
  readonly isAbandoning: ComputedRef<boolean>
  retry(): void
  saveEntry(entryId: string, input: EditImportPreviewEntryInput): Promise<ImportPreviewEntry>
  commit(): Promise<ImportJob>
  abandon(): Promise<ImportJob>
}

export function useImportJob(options: UseImportJobOptions): UseImportJob {
  const api = useApi()
  const queryClient = useQueryClient()
  const active = computed(
    () => options.enabled() && options.ledgerId() !== undefined && options.jobId() !== undefined,
  )

  const jobQuery = useQuery({
    queryKey: computed(() => importKeys.detail(options.ledgerId() ?? '', options.jobId() ?? '')),
    queryFn: ({ signal }) => api.imports.get(options.ledgerId()!, options.jobId()!, signal),
    enabled: active,
    retry: false,
  })

  const rawRowsQuery = useQuery({
    queryKey: computed(() => importKeys.rawRows(options.ledgerId() ?? '', options.jobId() ?? '')),
    queryFn: ({ signal }) => api.imports.rawRows(options.ledgerId()!, options.jobId()!, signal),
    enabled: active,
    retry: false,
  })

  const previewQuery = useQuery({
    queryKey: computed(() => importKeys.previewEntries(options.ledgerId() ?? '', options.jobId() ?? '')),
    queryFn: ({ signal }) => api.imports.previewEntries(options.ledgerId()!, options.jobId()!, signal),
    enabled: active,
    retry: false,
  })

  function invalidate(): Promise<void> {
    const ledgerId = options.ledgerId()
    if (ledgerId === undefined) return Promise.resolve()
    return Promise.all(
      importKeys.invalidatedByWrite(ledgerId).map((queryKey) => queryClient.invalidateQueries({ queryKey })),
    ).then(() => undefined)
  }

  const saveMutation = useMutation({
    mutationFn: ({ entryId, input }: { entryId: string; input: EditImportPreviewEntryInput }) =>
      api.imports.editPreviewEntry(options.ledgerId()!, entryId, input),
    onSuccess: () => invalidate(),
  })

  const commitMutation = useMutation({
    mutationFn: () => api.imports.commit(options.ledgerId()!, options.jobId()!),
    onSuccess: () => invalidate(),
  })

  const abandonMutation = useMutation({
    mutationFn: () => api.imports.abandon(options.ledgerId()!, options.jobId()!),
    onSuccess: () => invalidate(),
  })

  function saveEntry(entryId: string, input: EditImportPreviewEntryInput): Promise<ImportPreviewEntry> {
    return saveMutation.mutateAsync({ entryId, input })
  }

  // A flagged duplicate defaults to excluded, but stays fully overridable - the server itself
  // never sets it (`ImportService`, "excluded is never set for the reviewer"), so the default
  // is applied here, once, through the same edit a reviewer would make by hand. `decided` -
  // not "never edited since creation" - is the guard: resolving a raw row's counter account is
  // itself an edit and is very often what makes the duplicate flag computable in the first
  // place, so gating on an untouched `updatedAt` would almost never fire. Once an entry's id is
  // in `decided`, it is never auto-touched again for the rest of this page's lifetime, so a
  // reviewer's own choice - including un-excluding a duplicate they want to keep - always stands.
  const decided = new Set<string>()
  watch(
    () => previewQuery.data.value,
    (entries) => {
      for (const entry of entries ?? []) {
        if (entry.duplicateOfEntryId === undefined || decided.has(entry.id)) continue
        decided.add(entry.id)
        if (entry.excluded) continue
        void saveMutation.mutateAsync({
          entryId: entry.id,
          input: {
            entryDate: entry.entryDate,
            counterAccountId: entry.counterAccountId,
            amount: entry.amount,
            description: entry.description,
            excluded: true,
          },
        })
      }
    },
  )

  return {
    job: computed(() => jobQuery.data.value),
    rawRows: computed(() => rawRowsQuery.data.value ?? []),
    previewEntries: computed(() => previewQuery.data.value ?? []),
    isLoading: computed(
      () =>
        active.value &&
        (jobQuery.isPending.value || rawRowsQuery.isPending.value || previewQuery.isPending.value),
    ),
    error: computed(() => jobQuery.error.value ?? rawRowsQuery.error.value ?? previewQuery.error.value),
    notFound: computed(() => isNotFound(jobQuery.error.value)),
    retrying: retrying(jobQuery, rawRowsQuery, previewQuery),
    isSaving: computed(() => saveMutation.isPending.value),
    saveErrorDetail: computed(() => problemDetail(saveMutation.error.value)),
    isCommitting: computed(() => commitMutation.isPending.value),
    commitErrorDetail: computed(() => problemDetail(commitMutation.error.value)),
    commitErrorCode: computed(() => problemCode(commitMutation.error.value)),
    isAbandoning: computed(() => abandonMutation.isPending.value),
    retry: () => {
      void jobQuery.refetch()
      void rawRowsQuery.refetch()
      void previewQuery.refetch()
    },
    saveEntry,
    commit: () => commitMutation.mutateAsync(),
    abandon: () => abandonMutation.mutateAsync(),
  }
}
